import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InputComponent from './Template_Components/InputComponent';
import TextareaComponent from './Template_Components/TextAreaComponent';
import SelectComponent from './Template_Components/SelectComponent';
import MultiSelect from './Template_Components/MultiSelectComponent';
import AsyncSelect from './Template_Components/AsyncSelect';
import AsyncMultiSelect from './Template_Components/AsyncMultiSelect';
import DatePicker from './Template_Components/DatePicker';
import DateTimePicker from './Template_Components/DateTimePicker';
import FileInputWidget from './Template_Components/FileInputWidget';
import axios from 'axios';
import { API_ROUTES, API_DOMAIN } from './Config/apiConfig';
import { ToastContext } from '../../context/ToastContext';
import { useFormDraft } from '../../hooks/useFormDraft';
import * as listCache from '../../services/listCache';
import { fmtDateTime } from '../../utils/fmt';

// Extracts a short, safe user-facing message from an axios error.
// Never leaks HTML tracebacks, file paths, or Django debug output.
const extractErrorMessage = (error) => {
  const status = error?.response?.status;
  const data   = error?.response?.data;

  // No response at all — network failure
  if (!error?.response) {
    return 'Could not reach the server. Please check your connection and try again.';
  }

  // 5xx: server-side fault — never show internal details
  if (status >= 500) {
    return 'A server error occurred. Please try again or contact support if it persists.';
  }

  if (data) {
    // HTML page (Django debug traceback, nginx error page, etc.)
    if (typeof data === 'string' && data.trimStart().startsWith('<')) {
      return 'The server returned an unexpected response. Please try again.';
    }

    // DRF { detail: '...' }
    if (typeof data === 'object' && typeof data.detail === 'string') {
      const msg = data.detail.trim();
      return msg.length > 250 ? msg.slice(0, 247) + '…' : msg;
    }

    // Plain string message (safe, non-HTML)
    if (typeof data === 'string') {
      const msg = data.trim();
      return msg.length > 250 ? msg.slice(0, 247) + '…' : msg;
    }

    // DRF field validation errors: { fieldName: ['error msg'] }
    if (typeof data === 'object' && data !== null) {
      const parts = Object.entries(data)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => {
          const val = Array.isArray(v) ? v.join(', ') : String(v);
          const label = k === 'non_field_errors' ? '' : `${k}: `;
          return `${label}${val}`;
        });
      const msg = parts.join(' · ');
      return msg.length > 300 ? msg.slice(0, 297) + '…' : msg || 'An error occurred. Please try again.';
    }
  }

  return 'An error occurred. Please try again.';
};

const FormComponent = ({ fields, route, initialFormData, onSuccess, cancelPath, title, subtitle }) => {
  const [formData, setFormData] = useState(initialFormData || {});
  const [submitResult, setSubmitResult] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [draftBanner, setDraftBanner] = useState(null); // { data, savedAt }
  // Incrementing this key forces file widgets to remount (clears native input DOM value)
  const [fileResetKey, setFileResetKey] = useState(0);
  const navigate = useNavigate();
  const toast = useContext(ToastContext);

  const safeInitial = initialFormData || {};
  // Every "/…/input/:xId?" route has exactly one optional id param. Reading it
  // directly (instead of waiting for initialFormData.id from the async fetch)
  // gives a stable draft key from the very first render, so an edit session
  // never collides with a leftover "create new" draft under the same route.
  const routeParams = useParams();
  const routeIdParam = Object.values(routeParams).find(Boolean);
  const isEditing = !!routeIdParam || (Object.keys(safeInitial).length > 0 && !!safeInitial.id);

  const { saveDraft, loadDraft, clearDraft } = useFormDraft(route, routeIdParam || safeInitial.id);

  // On mount: check for a saved draft and prompt the user
  useEffect(() => {
    const draft = loadDraft();
    if (draft && draft.data && Object.keys(draft.data).length > 0) {
      setDraftBanner(draft);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When initialFormData arrives (e.g. after API fetch for edit), apply it
  // unless the user has already been shown the draft banner
  useEffect(() => {
    if (initialFormData && !draftBanner) {
      setFormData(initialFormData);
    }
  }, [initialFormData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRestoreDraft = () => {
    // File objects are non-serialisable — drafts store null for file fields.
    // Re-inject any existing file URLs from initialFormData so the widget
    // still shows "Current file: …" after restore.
    const fileFieldDefaults = fields
      .filter(f => f.type === 'file' && initialFormData?.[f.name])
      .reduce((acc, f) => ({ ...acc, [f.name]: initialFormData[f.name] }), {});
    setFormData({ ...draftBanner.data, ...fileFieldDefaults });
    setFileResetKey(k => k + 1);
    setDraftBanner(null);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setDraftBanner(null);
    setFormData(initialFormData || {});
    setFileResetKey(k => k + 1);
  };

  const handleChange = (fieldName, value) => {
    setFormData((prev) => {
      const next = { ...prev, [fieldName]: value };
      saveDraft(next);
      return next;
    });
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setSubmitResult(null);
    setLoading(true);

    try {
      const apiUrl = isEditing
        ? `${API_DOMAIN}${API_ROUTES[route]}${safeInitial.id}/`
        : `${API_DOMAIN}${API_ROUTES[route]}`;

      const token = localStorage.getItem('customer_management_token');
      const reqHeaders = {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Token ${token}` } : {}),
      };

      const dataFields = fields.filter((f) => f.type !== 'section');
      const hasFile = dataFields.some((f) => f.type === 'file' && formData[f.name]);
      let payload;

      if (hasFile) {
        reqHeaders['Content-Type'] = 'multipart/form-data';
        const fd = new FormData();
        dataFields.forEach((f) => {
          if (f.type === 'file' && formData[f.name]) fd.append(f.name, formData[f.name]);
          else if (formData[f.name] !== undefined && formData[f.name] !== null)
            fd.append(f.name, formData[f.name]);
        });
        payload = fd;
      } else {
        const json = {};
        dataFields.forEach((f) => {
          if (formData[f.name] !== undefined) {
            const val = formData[f.name];
            if (val === '' || val === null) {
              const isDateOrNumber = f.type === 'date' || f.type === 'datetime-local' || f.type === 'time' || f.type === 'number';
              const hasNumericOptions = f.type === 'select' && f.options?.some(opt => opt.value !== '' && opt.value !== null && !isNaN(Number(opt.value)));
              json[f.name] = (isDateOrNumber || hasNumericOptions || f.type === 'async-select') ? null : '';
            } else {
              json[f.name] = val;
            }
          }
        });
        payload = json;
        reqHeaders['Content-Type'] = 'application/json';
      }

      const method = isEditing ? axios.put : axios.post;
      const response = await method(apiUrl, payload, { headers: reqHeaders });

      setLoading(false);

      if (response.status === 200 || response.status === 201) {
        const successMsg = isEditing ? 'Record updated successfully.' : 'Record created successfully.';
        toast?.addToast({ type: 'success', message: successMsg });
        setSubmitResult({ success: true, message: successMsg });
        clearDraft(); // ← remove draft after successful save

        // Invalidate both the useApiCache (dropdown data) and ListTemplate
        // (list pages) caches for this route, so the next visit shows this change.
        const routeUrlPart = API_ROUTES[route];
        if (routeUrlPart) {
          listCache.invalidate((url) => url.includes(routeUrlPart));
        }

        if (onSuccess) {
          onSuccess(response.data);
        } else if (cancelPath) {
          setTimeout(() => navigate(cancelPath), 1200);
        }
      } else {
        throw new Error('Unexpected response from server.');
      }
    } catch (error) {
      setLoading(false);
      const msg = extractErrorMessage(error);
      toast?.addToast({ type: 'error', message: msg, duration: 6000 });
      setSubmitResult({ success: false, message: msg });
    }
  };

  const formatDraftTime = (isoString) => fmtDateTime(isoString);

  return (
    <form className="form-wrap" onSubmit={handleFormSubmit} encType="multipart/form-data">
      {(title || subtitle) && (
        <div className="form-card-header">
          <div>
            {title && <div className="form-card-title">{title}</div>}
            {subtitle && <div className="form-card-subtitle">{subtitle}</div>}
          </div>
        </div>
      )}

      {draftBanner && (
        <div className="form-draft-banner">
          <span className="form-draft-banner-icon">⚠</span>
          <span className="form-draft-banner-text">
            Unsaved draft found
            <span>— saved {formatDraftTime(draftBanner.savedAt)}</span>
          </span>
          <div className="form-draft-banner-actions">
            <button type="button" className="form-draft-btn-restore" onClick={handleRestoreDraft}>
              Restore
            </button>
            <button type="button" className="form-draft-btn-discard" onClick={handleDiscardDraft}>
              Discard
            </button>
          </div>
        </div>
      )}

      <div className="form-grid">
        {fields.map((field) => {
          if (field.type === 'section') {
            return (
              <div key={field.label} className="form-divider-label">
                {field.label}
              </div>
            );
          }

          const isWide = field.type === 'textarea' || field.type === 'multiselect' || field.type === 'async-multiselect' || field.wide;
          const isDouble = field.cols === 2;
          const wrapClass = `form-field${isWide ? ' span-full' : isDouble ? ' span-2' : ''}`;

          switch (field.type) {
            case 'textarea':
              return (
                <div key={field.name} className={wrapClass}>
                  <TextareaComponent
                    label={field.label}
                    value={formData[field.name] || field.value || ''}
                    onChange={(val) => handleChange(field.name, val)}
                    required={field.required}
                  />
                </div>
              );
            case 'select':
              return (
                <div key={field.name} className={wrapClass}>
                  <SelectComponent
                    label={field.label}
                    options={field.options}
                    value={formData[field.name] || ''}
                    onChange={(val) => handleChange(field.name, val)}
                    required={field.required}
                  />
                </div>
              );
            case 'async-select':
              return (
                <div key={field.name} className={wrapClass}>
                  <AsyncSelect
                    label={field.label}
                    endpoint={field.endpoint}
                    getOptionLabel={field.getOptionLabel}
                    getOptionValue={field.getOptionValue}
                    initialLabel={field.initialLabel}
                    searchParam={field.searchParam}
                    minChars={field.minChars}
                    pageSize={field.pageSize}
                    placeholder={field.placeholder}
                    value={formData[field.name] || ''}
                    onChange={(val) => handleChange(field.name, val)}
                    required={field.required}
                  />
                </div>
              );
            case 'async-multiselect':
              return (
                <div key={field.name} className={wrapClass}>
                  <AsyncMultiSelect
                    label={field.label}
                    endpoint={field.endpoint}
                    getOptionLabel={field.getOptionLabel}
                    getOptionValue={field.getOptionValue}
                    searchParam={field.searchParam}
                    minChars={field.minChars}
                    pageSize={field.pageSize}
                    placeholder={field.placeholder}
                    value={formData[field.name] || []}
                    onChange={(val) => handleChange(field.name, val)}
                    required={field.required}
                  />
                </div>
              );
            case 'file':
              return (
                <div key={`file-${field.name}-${fileResetKey}`} className={wrapClass}>
                  <FileInputWidget
                    label={field.label}
                    required={field.required}
                    value={formData[field.name] ?? null}
                    onChange={(file) => handleChange(field.name, file)}
                  />
                </div>
              );
            case 'checkbox':
              return (
                <div key={field.name} className={wrapClass}>
                  <InputComponent
                    label={field.label}
                    type="checkbox"
                    checked={formData[field.name] || false}
                    onChange={(e) => handleChange(field.name, e.target.checked)}
                  />
                </div>
              );
            case 'multiselect':
              return (
                <div key={field.name} className={wrapClass}>
                  <MultiSelect
                    label={field.label}
                    options={field.options}
                    value={formData[field.name] || []}
                    onChange={(sel) => handleChange(field.name, sel)}
                  />
                </div>
              );
            case 'date':
              return (
                <div key={field.name} className={wrapClass}>
                  <label className="form-label">{field.label}{field.required && <span className="req">*</span>}</label>
                  <DatePicker
                    value={formData[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    placeholder={field.placeholder || 'Select date'}
                    required={field.required}
                    name={field.name}
                    dateConstraint={field.dateConstraint}
                  />
                </div>
              );
            case 'datetime-local':
              return (
                <div key={field.name} className={wrapClass}>
                  <label className="form-label">{field.label}{field.required && <span className="req">*</span>}</label>
                  <DateTimePicker
                    value={formData[field.name] || ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    placeholder={field.placeholder || 'Select date & time'}
                    required={field.required}
                    name={field.name}
                    dateConstraint={field.dateConstraint}
                  />
                </div>
              );
            default:
              return (
                <div key={field.name} className={wrapClass}>
                  <InputComponent
                    label={field.label}
                    type={field.type}
                    value={formData[field.name] ?? field.defaultValue ?? field.value ?? ''}
                    onChange={(e) => handleChange(field.name, e.target.value)}
                    readOnly={field.readOnly}
                    required={field.required}
                  />
                </div>
              );
          }
        })}
      </div>

      <div className="form-footer" style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 12 }}>
        {submitResult && (
          <span
            className={`alert ${submitResult.success ? 'alert-success' : 'alert-error'}`}
            style={{ marginTop: 0, padding: '6px 12px', marginRight: 'auto' }}
          >
            {submitResult.message}
          </span>
        )}

        {cancelPath && (
          <button type="button" className="btn btn-secondary" onClick={() => navigate(cancelPath)}>
            <svg width="11" height="11" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0}}><path d="M3 3l10 10M13 3L3 13"/></svg>
            Cancel
          </button>
        )}

        <button type="button" className="btn btn-danger" onClick={() => { setFormData(initialFormData || {}); setFileResetKey(k => k + 1); }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" style={{flexShrink:0}}><path d="M13.5 4A6 6 0 1 0 14 9"/><path d="M14 2v3h-3" strokeLinejoin="round"/></svg>
          Reset
        </button>

        <button type="submit" className="btn btn-primary" disabled={isLoading}>
          {isLoading ? (
            <><span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} /> Saving…</>
          ) : isEditing ? (
            <><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0}}><path d="M2 8l5 5L14 3"/></svg> Update Record</>
          ) : (
            <><svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" style={{flexShrink:0}}><path d="M8 3v10M3 8h10"/></svg> Create Record</>
          )}
        </button>
      </div>
    </form>
  );
};

export default FormComponent;
